import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './TagSelector.css';

// Shared cache for tags across all TagSelector instances
let tagsCache = null;
let tagsCachePromise = null;
let tagsCacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function TagSelector({ selectedTagIds = [], onChange, disabled = false, initialTags = [] }) {
  const [tags, setTags] = useState(initialTags);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const dropdownElementRef = useRef(null);
  const buttonRef = useRef(null);

  // Update tags when initialTags prop changes
  useEffect(() => {
    if (initialTags && initialTags.length > 0) {
      setTags(prev => {
        // Merge initialTags into existing tags without losing fetched tags
        const existingIds = new Set(prev.map(t => t.id));
        const newTags = initialTags.filter(t => !existingIds.has(t.id));
        return newTags.length > 0 ? [...prev, ...newTags] : prev;
      });
    }
  }, [initialTags]);

  // Fetch all tags when dropdown opens
  useEffect(() => {
    if (isOpen && !loading) {
      fetchTags();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        dropdownElementRef.current && !dropdownElementRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScrollOrResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleScrollOrResize);
      window.addEventListener('scroll', handleScrollOrResize, true);
      requestAnimationFrame(() => {
        setTimeout(() => {
          calculateDropdownPosition();
        }, 10);
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [isOpen, loading, tags]);

  const calculateDropdownPosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const dropW = 260;
    const dropH = 320;
    const margin = 8;

    let top = rect.bottom + margin;
    let left = rect.left;

    if (left + dropW > window.innerWidth - margin) left = rect.right - dropW;
    if (left < margin) left = margin;
    if (top + dropH > window.innerHeight - margin) top = rect.top - dropH - margin;
    if (top < margin) top = margin;

    setDropdownCoords({ top, left });
  };

  const fetchTags = async () => {
    const now = Date.now();
    if (tagsCache && (now - tagsCacheTimestamp) < CACHE_DURATION) {
      setTags(tagsCache);
      return;
    }

    if (tagsCachePromise) {
      try {
        const cachedTags = await tagsCachePromise;
        setTags(cachedTags);
        return;
      } catch (err) {
        // fall through to own fetch
      }
    }

    try {
      setLoading(true);
      tagsCachePromise = axios.get(`${API_URL}/portfolio/tags/?page_size=all`);
      const response = await tagsCachePromise;
      const data = response.data.results || response.data;
      const tagsArray = Array.isArray(data) ? data : [];

      tagsCache = tagsArray;
      tagsCacheTimestamp = Date.now();
      tagsCachePromise = null;

      setTags(tagsArray);
    } catch (err) {
      console.error('Error fetching tags:', err);
      tagsCachePromise = null;
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tagId) => {
    if (disabled) return;
    const newSelectedIds = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter(id => id !== tagId)
      : [...selectedTagIds, tagId];
    onChange(newSelectedIds);
  };

  // Derive selected tags from what we know (initialTags available immediately, full list after fetch)
  const allAvailableTags = tags.length > 0 ? tags : (initialTags || []);
  const selectedTags = allAvailableTags.filter(tag => selectedTagIds.includes(tag.id));

  // Sort: selected tags first in dropdown
  const sortedTags = [...tags].sort((a, b) => {
    const aSelected = selectedTagIds.includes(a.id) ? 1 : 0;
    const bSelected = selectedTagIds.includes(b.id) ? 1 : 0;
    return bSelected - aSelected;
  });

  const maxDots = 3;
  const visibleTags = selectedTags.slice(0, maxDots);
  const extraCount = selectedTags.length - maxDots;

  return (
    <div className="tag-selector" ref={dropdownRef}>
      <div className="tag-selector-display">
        <div className="tag-dots">
          {visibleTags.map(tag => (
            <span
              key={tag.id}
              className="tag-dot"
              style={{ background: tag.color }}
              title={tag.name}
            />
          ))}
          {extraCount > 0 && (
            <span className="tag-overflow" title={selectedTags.slice(maxDots).map(t => t.name).join(', ')}>
              +{extraCount}
            </span>
          )}
          {selectedTags.length === 0 && (
            <span className="no-tags">—</span>
          )}
        </div>
        {!disabled && (
          <button
            ref={buttonRef}
            className="tag-selector-button"
            onClick={() => setIsOpen(!isOpen)}
            type="button"
            title="Manage tags"
          >
            ✏
          </button>
        )}
      </div>

      {isOpen && !disabled && (
        <div
          ref={dropdownElementRef}
          className="tag-selector-dropdown"
          style={{ position: 'fixed', top: dropdownCoords.top, left: dropdownCoords.left }}
        >
          <div className="tag-dropdown-header">
            <span className="tag-dropdown-title">Tags</span>
            <button className="tag-dropdown-close" onClick={() => setIsOpen(false)} type="button">×</button>
          </div>
          {loading ? (
            <div className="tag-dropdown-empty">Loading tags…</div>
          ) : tags.length === 0 ? (
            <div className="tag-dropdown-empty">No tags available</div>
          ) : (
            <div className="tag-list">
              {sortedTags.map(tag => {
                const isSelected = selectedTagIds.includes(tag.id);
                return (
                  <label
                    key={tag.id}
                    className={`tag-option ${isSelected ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTagToggle(tag.id)}
                    />
                    <span className="tag-dot" style={{ background: tag.color }} />
                    <span className="tag-option-name">{tag.name}</span>
                    {tag.category && (
                      <span className="tag-group">{tag.category}</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TagSelector;
